import React from "react";
import {
  Box,
  Container,
  Typography,
  Divider,
  Paper,
  Card,
} from "@mui/material";
import Htag from "../components/typography/Htag";
import Flx from "../components/layout/Flx";
import DashboardLogo from "../assets/DashboardLogo";

const ACKNOWLEDGMENTS = [
  'By accessing, registering for, signing in to, or using the Lendmarq borrower portal (the "Portal"), you acknowledge and agree that your use of the Portal is solely in connection with a loan for business, commercial, or investment purposes and not for any personal, family, or household use.',
  "You further acknowledge and agree that any property securing a loan must be non-owner occupied and used strictly for investment or business purposes. Lendmarq does not originate or offer consumer-purpose loans, and the Portal is not intended for consumer use.",
  "You represent that all information you provide through the Portal is true, complete, and accurate, and that you have full authority to act on behalf of the applicable borrower entity.",
  "You consent to receive all communications, disclosures, and notices electronically and agree that electronic signatures and records shall be binding and enforceable to the fullest extent permitted by law.",
  "You acknowledge that Lendmarq and its affiliates, investors, financing partners, and servicers may rely on all information submitted through the Portal in connection with underwriting, servicing, financing, sale, participation, or securitization of any loan.",
];

const SECTIONS = [
  {
    num: 1,
    title: "Overview and Acceptance of Terms",
    paragraphs: [
      'These Borrower Portal Terms and Conditions (these "Terms") govern your access to and use of the Portal operated by Lendmarq LLC and its affiliates (collectively, "Lendmarq," "we," "us," or "our").',
      "The Portal is an online platform designed to facilitate loan application, underwriting, document submission, communication, and servicing-related interactions.",
      "By accessing or using the Portal, you agree to be bound by these Terms. If you do not agree, you must not access or use the Portal.",
      "Your use of the Portal is also subject to Lendmarq's Privacy Policy, which is incorporated herein by reference.",
    ],
  },
  {
    num: 2,
    title: "Business Purpose Only",
    paragraphs: [
      "The Portal is made available exclusively for commercial, business, and investment lending activities.",
      "You represent and warrant that all loans requested or managed through the Portal are for business or investment purposes only and not for personal, family, or household use. You further represent that any property securing a loan is non-owner occupied and not used as a primary or secondary residence.",
      "You acknowledge that Lendmarq does not provide consumer-purpose loans and that any attempt to use the Portal for consumer purposes is strictly prohibited.",
    ],
  },
  {
    num: 3,
    title: "No Offer, Commitment, or Reliance",
    paragraphs: [
      "All information made available through the Portal is provided for general informational purposes only and does not constitute an offer, solicitation, commitment, or approval to lend.",
      "Any potential loan is subject to Lendmarq's full underwriting process, credit approval, satisfaction of all conditions precedent, and execution of definitive loan documents.",
      "Lendmarq reserves the right to modify, withdraw, or correct any information in the Portal at any time without notice. You agree that you will not rely on the Portal as a final determination of loan terms, approval status, or funding decisions.",
    ],
  },
  {
    num: 4,
    title: "Portal Not System of Record",
    paragraphs: [
      "The Portal is provided solely as a convenience tool and does not constitute the official system of record for any loan.",
      "Information displayed in the Portal, including loan status, balances, terms, conditions, or approvals, may be incomplete, delayed, or inaccurate. The definitive and controlling terms of any loan are contained exclusively in the executed loan documents and the official records maintained by the applicable servicer.",
      "In the event of any inconsistency between the Portal and such documents or records, the loan documents and servicing records shall govern.",
      "You expressly agree not to rely on the Portal for financial, legal, or investment decisions.",
    ],
  },
  {
    num: 5,
    title: "Account Registration and Security",
    paragraphs: [
      "To access certain features of the Portal, you may be required to register for an account and provide certain information. You agree to provide accurate, current, and complete information and to update such information as necessary.",
      "You are solely responsible for maintaining the confidentiality and security of your login credentials. You agree that you will not share, disclose, transfer, or permit access to your account credentials to any other person or entity, including employees, agents, brokers, or consultants, unless expressly authorized in writing by Lendmarq.",
      "You further agree not to permit any third party to access or use the Portal on your behalf using your credentials.",
      "You are responsible for all activity that occurs under your account, whether authorized or unauthorized.",
      "You agree to immediately notify Lendmarq of any actual or suspected unauthorized access, use, or compromise of your account or credentials.",
      "You acknowledge and agree that you shall be liable to Lendmarq for any and all losses, damages, costs, or expenses (including reasonable attorneys' fees) arising out of or relating to your breach of this Section, any unauthorized sharing or misuse of credentials, or any improper or unauthorized use of the Portal through your account.",
      "To the fullest extent permitted by law, Lendmarq shall not be liable for any loss or damage arising from unauthorized access to your account, except to the extent resulting from Lendmarq's gross negligence or willful misconduct.",
      "Lendmarq reserves the right to suspend, restrict, or terminate access to the Portal at any time for security, compliance, or operational reasons.",
    ],
  },
  {
    num: 6,
    title: "User Information and Representations",
    paragraphs: [
      "You represent and warrant that all information and materials submitted through the Portal are true, accurate, complete, and not misleading, and that no material information has been omitted.",
      "You further represent that you have full authority to submit such information and that no fraud, misrepresentation, or deceptive conduct has occurred in connection with your use of the Portal or any loan request.",
      "By submitting information through the Portal, you grant Lendmarq the right to use, store, analyze, and share such information in connection with underwriting, servicing, loan sale, participation, securitization, financing arrangements, investor reporting, and compliance or risk monitoring.",
    ],
  },
  {
    num: 7,
    title: "Electronic Communications and Signatures",
    paragraphs: [
      "You consent to conduct business electronically and to receive all communications, disclosures, and notices through the Portal or via email.",
      "Any communication delivered through the Portal or to your designated email address shall be deemed received when sent.",
      "You agree that electronic signatures and records shall have the same legal force and effect as original signatures and paper documents.",
      "Electronic records maintained by Lendmarq shall be deemed accurate and admissible as evidence to the fullest extent permitted by law.",
    ],
  },
  {
    num: 8,
    title: "Third-Party Services and Data",
    paragraphs: [
      "The Portal may utilize or rely on third-party service providers, including credit reporting agencies, identity verification providers, financial data aggregators, valuation services, and compliance vendors.",
      "Lendmarq does not control such third parties and does not guarantee the accuracy, completeness, or availability of their data or services. Lendmarq may rely on such information in its sole discretion.",
    ],
  },
  {
    num: 9,
    title: "Monitoring, Verification, and Compliance",
    paragraphs: [
      "Lendmarq reserves the right to verify any information submitted, request additional documentation, and monitor activity within the Portal for fraud prevention, security, compliance, anti-money laundering, and sanctions screening purposes.",
      "Lendmarq has no obligation to monitor your use of the Portal or to review any submitted information and shall have no liability for failing to do so.",
      "Lendmarq may investigate suspected misuse or unlawful activity and may report such activity to regulatory or law enforcement authorities.",
    ],
  },
  {
    num: 10,
    title: "OFAC and Legal Compliance",
    paragraphs: [
      "You represent and warrant that you are not subject to sanctions administered by the U.S. Department of the Treasury's Office of Foreign Assets Control and that you are not located in, organized in, or acting on behalf of any sanctioned jurisdiction.",
      "You further represent that all funds used in connection with any transaction are derived from lawful sources and that your activities comply with applicable laws and regulations.",
    ],
  },
  {
    num: 11,
    title: "Loan Sales, Transfers, and Servicing",
    paragraphs: [
      "You acknowledge that any loan may be sold, assigned, participated, financed, or securitized at any time without prior notice, except as required by applicable law.",
      "Servicing rights may also be transferred. You agree that your information may be shared with investors, purchasers, warehouse lenders, servicers, subservicers, and other related parties in connection with such transactions.",
      "Portal functionality and access may be modified, limited, or discontinued in connection with any such transfer.",
    ],
  },
  {
    num: 12,
    title: "Reliance on Portal Instructions",
    paragraphs: [
      "Lendmarq shall be entitled to rely on any instructions, requests, or submissions made through the Portal using your account as authorized, authentic, and binding upon you, without further verification.",
      "You acknowledge that such instructions may include, without limitation, requests relating to loan activity, disbursements, communications, or servicing matters.",
      "Lendmarq reserves the right, in its sole discretion, to reject, disregard, delay, or require additional verification of any instruction or submission.",
    ],
  },
  {
    num: 13,
    title: "Permitted Use",
    paragraphs: [
      "You agree to use the Portal solely for lawful purposes and in accordance with these Terms. You shall not attempt to gain unauthorized access to any systems or data, interfere with the operation of the Portal, or engage in any activity that could damage, disable, or impair the Portal.",
    ],
  },
  {
    num: 14,
    title: "Intellectual Property",
    paragraphs: [
      "All content, software, systems, and materials available through the Portal are owned by Lendmarq or its licensors and are protected by applicable intellectual property laws. You are granted a limited, non-exclusive, non-transferable right to use the Portal in accordance with these Terms.",
    ],
  },
  {
    num: 15,
    title: "Disclaimer of Warranties",
    isDisclaimer: true,
    paragraphs: [
      'THE PORTAL IS PROVIDED "AS IS" AND "AS AVAILABLE." LENDMARQ DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND ACCURACY OR COMPLETENESS OF INFORMATION.',
    ],
  },
  {
    num: 16,
    title: "Limitation of Liability",
    paragraphs: [
      "To the maximum extent permitted by law, Lendmarq shall not be liable for any indirect, incidental, consequential, special, or punitive damages, including loss of profits, data, business opportunities, or goodwill.",
      "Lendmarq shall not be liable for any failure or delay arising from system outages, interruptions, cyber incidents, or events beyond its reasonable control.",
    ],
  },
  {
    num: 17,
    title: "Indemnification",
    paragraphs: [
      "You agree to indemnify, defend, and hold harmless Lendmarq and its affiliates from and against any claims, losses, damages, liabilities, and expenses arising out of or related to your use of the Portal, your breach of these Terms, or any inaccurate or misleading information you provide.",
    ],
  },
  {
    num: 18,
    title: "No Fiduciary Relationship",
    paragraphs: [
      "Nothing in these Terms creates or shall be deemed to create any fiduciary, advisory, agency, partnership, or joint venture relationship between you and Lendmarq.",
    ],
  },
  {
    num: 19,
    title: "Dispute Resolution; Arbitration; Waivers",
    paragraphs: [
      "Any dispute arising out of or relating to these Terms or the Portal shall be resolved by binding arbitration.",
      "You waive any right to a jury trial and agree not to bring claims as part of any class or representative action.",
    ],
  },
  {
    num: 20,
    title: "Jurisdictional Limitations",
    paragraphs: [
      "The Portal is not intended to solicit or offer loans in any jurisdiction where such activity would require licensing or be prohibited. The Portal is not intended for consumer lending.",
    ],
  },
  {
    num: 21,
    title: "Assignment",
    paragraphs: [
      "Lendmarq may assign these Terms without notice. You may not assign these Terms without prior written consent.",
    ],
  },
  {
    num: 22,
    title: "Survival",
    paragraphs: [
      "All provisions relating to liability, indemnification, dispute resolution, data usage, and other provisions which by their nature should survive shall survive termination of these Terms.",
    ],
  },
  {
    num: 23,
    title: "Modifications",
    paragraphs: [
      "Lendmarq reserves the right to modify these Terms at any time. Updated Terms will be effective upon posting. Your continued use of the Portal constitutes acceptance of such modifications.",
    ],
  },
];

const sectionTitleSx = {
  fontWeight: 700,
  fontSize: "16px",
  textTransform: "uppercase",
  color: "primary.main",
  mb: 1.5,
  letterSpacing: "0.02em",
};

const paragraphSx = {
  fontSize: "0.875rem",
  color: "text.secondary",
  lineHeight: 1.7,
  mb: 1.5,
  textAlign: "justify",
};

const TermsOfUseScreen = () => {
  return (
    <Box sx={{ bgcolor: "#f5f8fa", minHeight: "100vh", py: 4 }}>
      <Container
        maxWidth="md"
        sx={{ background: "#ffffff", padding: "0 !important" }}
      >
        <Card sx={{ p: 4, py: 5 }}>
          {/* Page Title */}
          <Flx center column gap={1} sx={{ mb: 2 }}>
            <DashboardLogo />
            <Htag
              // variant="h6"

              sx={{
                textAlign: "center",
                fontWeight: 700,
                // textTransform: "uppercase",
                // letterSpacing: "0.04em",
                mb: 1,
              }}
            >
              Borrower Portal Terms and Conditions
            </Htag>
          </Flx>

          <Divider sx={{ mb: 4, mt: 2 }} />

          {/* Acknowledgments */}
          <Box sx={{ mb: 4 }}>
            <Typography sx={sectionTitleSx}>
              User Acknowledgments and Consents
            </Typography>
            {ACKNOWLEDGMENTS.map((text, i) => (
              <Typography key={i} sx={paragraphSx}>
                {text}
              </Typography>
            ))}
          </Box>

          {/* Sections */}
          {SECTIONS.map((section) => (
            <Paper
              key={section.num}
              elevation={0}
              sx={{
                mb: 3,
                p: 3,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <Typography sx={sectionTitleSx}>
                {section.num}. {section.title}
              </Typography>
              {section.paragraphs.map((text, i) => (
                <Typography
                  key={i}
                  sx={{
                    ...paragraphSx,
                    ...(section.isDisclaimer && {
                      textTransform: "uppercase",
                      fontSize: "0.8rem",
                      letterSpacing: "0.01em",
                    }),
                    mb: i < section.paragraphs.length - 1 ? 1.5 : 0,
                  }}
                >
                  {text}
                </Typography>
              ))}
            </Paper>
          ))}

          {/* Footer */}
          <Divider sx={{ mt: 2, mb: 2 }} />
          <Typography
            variant="caption"
            align="center"
            display="block"
            sx={{ color: "text.disabled" }}
          >
            &copy; Lendmarq LLC. All rights reserved.
          </Typography>
        </Card>
      </Container>
    </Box>
  );
};

export default TermsOfUseScreen;
